import { isKnownAgentEvent, } from "./events";
import { Run } from "./run";
export class AgentThread {
    constructor(agent) {
        this.userListeners = [];
        this.feedbackRequestHandlers = [];
        this.activeRun = null;
        this.agent = agent;
        this.threadId = this.generateThreadId();
    }
    generateThreadId() {
        if (typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
        return `thread_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
    /**
     * Install the single subscription-level listener that fans out to all
     * user `listen` callbacks and the active `Run`. Idempotent — first caller
     * wins; subsequent calls return the same promise.
     */
    ensureMasterListener() {
        if (this.masterListenerPromise)
            return this.masterListenerPromise;
        this.masterListenerPromise = (async () => {
            const sub = await this.agent._getSubscription();
            sub.listen((raw) => this._dispatch(raw));
        })();
        return this.masterListenerPromise;
    }
    _dispatch(raw) {
        var _a, _b, _c;
        let evt = raw && typeof raw === "object" && "event" in raw && "content" in raw
            ? raw
            : { event: "agent_output", content: (raw !== null && raw !== void 0 ? raw : {}) };
        // Phase 6: surface wire-level transport errors as typed `agent_error`
        // so the active Run rejects and user listeners get a normalized envelope.
        if (evt.event === "error" || evt.event === "transport_error") {
            const c = ((_a = evt.content) !== null && _a !== void 0 ? _a : {});
            const message = typeof c === "object"
                ? (_c = (_b = c["message"]) !== null && _b !== void 0 ? _b : c["error"]) !== null && _c !== void 0 ? _c : "WebSocket error"
                : String(c);
            evt = {
                event: "agent_error",
                content: {
                    type: "agent_error_response",
                    status: "error",
                    code: "TRANSPORT_ERROR",
                    message,
                    details: c,
                    thread_id: this.threadId,
                    ref_id: "",
                    agent_id: this.agent.agentId,
                    reply_to: "",
                },
            };
        }
        if (!isKnownAgentEvent(evt) && typeof console !== "undefined") {
            console.warn(`[adk] unknown agent event "${evt.event}" — passing through untyped. ` +
                `Add it to AGENT_EVENTS in agentic/events.ts to type it.`);
        }
        if (this.activeRun) {
            try {
                this.activeRun._push(evt);
            }
            catch (e) {
                if (typeof console !== "undefined") {
                    console.error("[adk] active run dispatch failed:", e);
                }
            }
        }
        for (const callback of this.userListeners) {
            try {
                callback(evt);
            }
            catch (e) {
                if (typeof console !== "undefined") {
                    console.error("[adk] user listen callback threw:", e);
                }
            }
        }
    }
    /**
     * Subscribe to typed events from this agent's channel.
     *
     * Multiple `listen` callbacks may be registered; each receives every event.
     * The callback receives a discriminated `AgentEventOrUnknown` envelope —
     * `evt.event` narrows `evt.content` to the matching shape (`AgentOutput`,
     * `AgentError`, `HumanInputRequest`, etc.). Unknown event names are logged
     * via `console.warn` and forwarded as `UnknownAgentEvent`.
     */
    async listen(callback) {
        await this.ensureMasterListener();
        this.userListeners.push(callback);
    }
    /**
     * Register a handler invoked whenever a `human_input_request` arrives for
     * an active run. The handler is responsible for collecting input from the
     * user and calling `run.sendFeedback(value)` to continue the loop.
     */
    feedbackRequest(handler) {
        this.feedbackRequestHandlers.push(handler);
    }
    /** @internal — invoked by Run when a human_input_request arrives. */
    _fireRequestFeedback(req, run) {
        for (const feedbackHandler of this.feedbackRequestHandlers) {
            try {
                const r = feedbackHandler(req, run);
                // tolerate both sync and async handlers; surface async errors
                if (r && typeof r.then === "function") {
                    r.catch((e) => {
                        if (typeof console !== "undefined") {
                            console.error("[adk] onHumanInput handler rejected:", e);
                        }
                    });
                }
            }
            catch (e) {
                if (typeof console !== "undefined") {
                    console.error("[adk] onHumanInput handler threw:", e);
                }
            }
        }
    }
    /** @internal — clear active run on terminal event. */
    _closeRun(run) {
        if (this.activeRun === run)
            this.activeRun = null;
    }
    /** @internal — send a `user_reply` carrying `reply_id`. */
    async _sendReply(value, replyId) {
        const sub = await this.agent._getSubscription();
        const content = {
            user_input: value,
            thread_id: this.threadId,
            reply_id: replyId,
        };
        // Subscription.push declares Promise<void> but actually resolves with
        // the generated ref_id; cast through unknown for type safety.
        const ack = (await sub.push("user_reply", content));
        return typeof ack === "string" ? ack : "";
    }
    /**
     * Start a new run on this thread. Returns a `Run` handle whose `done()`
     * promise resolves with the terminal `AgentOutput` or rejects with an
     * `AgentError`. Multi-turn HITL stays inside this run; respond via
     * `run.sendFeedback(value)` or by registering `onHumanInput`.
     *
     * If a previous run is still active, it is force-closed with a warning —
     * each thread may only have one active run at a time.
     */
    async run(user_input, deps) {
        await this.ensureMasterListener();
        if (this.activeRun && !this.activeRun.isClosed()) {
            if (typeof console !== "undefined") {
                console.warn("[adk] starting new run while previous run is still active — closing previous");
            }
            this.activeRun._close("Superseded by new run on the same thread");
        }
        const run = new Run(this);
        this.activeRun = run;
        const sub = await this.agent._getSubscription();
        const event = (deps === null || deps === void 0 ? void 0 : deps.reply_id) ? "user_reply" : "user_input";
        const content = {
            user_input,
            thread_id: this.threadId,
        };
        if (deps === null || deps === void 0 ? void 0 : deps.reply_id)
            content.reply_id = deps.reply_id;
        // Subscription.push declares Promise<void> but resolves with ref_id.
        const ack = (await sub.push(event, content));
        run._setRefId(typeof ack === "string" ? ack : "");
        return run;
    }
}
